// "Ask ReNurture" — the librarian, embedded (PROTOTYPE, flag-gated by ASK_RENURTURE).
// She asks in her own words; a local brain server understands the need and returns
// REAL ReNurture content ids, gated by her actual progress (passed in `unlocked`).
// This component only renders + dispatches to the app's real open handlers — it
// generates no prose and invents no content. Crisis words route to support first.
//
// State (query + result) is LIFTED to App.tsx and passed in, so opening a piece of
// content and returning keeps the answer live to pick a different option.
//
// Styling mirrors the app's premium surfaces (article tiles + toolkit cards):
// Fraunces serif titles, per-content-type accent colours, warm card gradients,
// uppercase eyebrow labels. Tokens fall back to theme.css :root values.
import { useEffect, useState } from 'react';
import { ASK_BRAIN_URL, TRIAGE_V2 } from '@/config/features';
import { BackButton } from '@/components/BackButton';
import { isCrisis } from '@/services/crisisFloor';
import { writeFreeRemaining } from '@/new/askFreeCache';

// When the hourly question allowance runs out, the server says exactly how long is left.
// Persisted so closing and reopening Ask (or reloading) shows the same honest countdown
// instead of letting the user spend another request discovering they are still blocked.
// First-use notice. Ask is a new, still-developing feature that answers in its own
// words about a subject where being wrong matters, so the first thing anyone sees is a
// plain statement of what it is and is not. Acknowledged once per device.
const BETA_KEY = 'renurture.ask.beta.v1';
const betaAccepted = (): boolean => {
  try { return localStorage.getItem(BETA_KEY) === '1'; } catch { return true; } // storage blocked → don't trap the user
};
const acceptBeta = () => {
  try { localStorage.setItem(BETA_KEY, '1'); } catch { /* ignore */ }
};

const BLOCKED_KEY = 'renurture.ask.blockedUntil';
const readBlockedUntil = (): number => {
  try { return Number(localStorage.getItem(BLOCKED_KEY)) || 0; } catch { return 0; }
};
const writeBlockedUntil = (ms: number) => {
  try { localStorage.setItem(BLOCKED_KEY, String(ms)); } catch { /* ignore */ }
};

/** "1 hour", "42 minutes", "30 seconds" — the wait in the largest sensible unit, so the
 *  countdown reads like a person speaking rather than a stopwatch. */
function describeWait(sec: number): string {
  if (sec >= 90) {
    const mins = Math.ceil(sec / 60);
    if (mins >= 60) {
      const hrs = Math.round(mins / 60);
      return `${hrs} hour${hrs === 1 ? '' : 's'}`;
    }
    return `${mins} minute${mins === 1 ? '' : 's'}`;
  }
  const s = Math.max(1, sec);
  return `${s} second${s === 1 ? '' : 's'}`;
}

type Card = { kind: 'read' | 'tool' | 'lesson' | 'breathe' | 'meditation'; id: string; title: string; why?: string };
type Result = any;

// Per-content-type accents, all drawn from the app palette (theme.css / library.ts /
// toolkit.ts): breathe = Calm blue, tool = green, read = Rebuild terracotta, lesson =
// amber, meditation = Hold lavender (guided, self-compassion — distinct from breathe).
const KIND: Record<string, { label: string; accent: string; soft: string }> = {
  lesson:     { label: 'Lesson',     accent: '#9b6712', soft: 'rgba(200,134,26,0.12)' },
  read:       { label: 'Read',       accent: '#95591f', soft: 'rgba(193,125,64,0.12)' },
  tool:       { label: 'Tool',       accent: '#167a5a', soft: 'rgba(29,158,117,0.12)' },
  breathe:    { label: 'Breathe',    accent: '#356275', soft: 'rgba(93,138,158,0.14)' },
  meditation: { label: 'Meditation', accent: '#6a5cb0', soft: 'rgba(106,92,176,0.13)' },
};
const kindOf = (k: string) => KIND[k] || KIND.lesson;

const SERIF = 'Fraunces, Georgia, serif';
const UI = 'Inter, system-ui, -apple-system, sans-serif';

const EXAMPLES = [
  'my ex keeps calling', 'how do I stop feeling guilty', 'I can’t sleep',
  'I feel numb', 'how do I stay calm at work?', 'I think I am at risk',
];

// Strip a leaked catalogue category prefix (e.g. "Meditation: Soothing breathing",
// "Key Learning: Understanding Guilt", "KEY Exercise: Soothing shame"). Only strips
// a short leading "Label:" segment when it looks like a category, never a real title.
const PREFIX_WORDS = /\b(key|meditation|lesson|exercise|reading|learning|article|reflection|tool|video|audio|faq|breathing|module|part|chapter)\b/i;
function cleanTitle(t: string): string {
  const s = (t || '').trim();
  const m = s.match(/^([^:]{2,24}):\s*(.+)$/);
  return m && PREFIX_WORDS.test(m[1]) ? m[2].trim() : s;
}
function joinAnd(list: string[]): string {
  const a = list.map(cleanTitle).filter(Boolean);
  if (a.length <= 1) return a[0] || '';
  return a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1];
}

export function AskReNurture(props: {
  onClose: () => void;
  hasChildren: boolean;
  unlocked: string[];
  // Supabase access token — the /ask endpoint is requireUser-gated (member feature +
  // calls paid vendors), so the request must carry it. Null when signed out / on a
  // local trial session; the guide then shows a sign-in prompt instead of calling.
  authToken: string | null;
  // True when the user has granted research consent. Gates BOTH the capture of their
  // question and the feedback row: a user who has not consented is never asked to rate,
  // because there is nothing stored for the rating to attach to.
  researchConsented?: boolean;
  query: string;
  result: Result | null;
  setQuery: (s: string) => void;
  setResult: (r: Result | null) => void;
  onOpenLesson: (id: string) => void;
  onOpenArticle: (id: string) => void;
  onOpenTool: (id: string) => void;
  onOpenBreath: (id: string) => void;
  onOpenMeditation: (id: string) => void;
  onOpenCalm: () => void;
  /** Opens the app's membership/unlock wall (shown on a 403 membership_required). */
  onUnlock?: () => void;
  /** Verified user id, only used to key the cached free-question count. */
  userId?: string | null;
}) {
  const { query: q, result: r, setQuery: setQ, setResult: setR } = props;
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // Set by a 403 from /ask: a calm notice instead of the red error box.
  const [notice, setNotice] = useState<'membership' | 'paused' | null>(null);
  // Which rating this answer was given, if any. Local to the answer on screen: cleared
  // whenever a new question is asked, so the row comes back for the new answer.
  const [rated, setRated] = useState<string | null>(null);
  const [beta, setBeta] = useState<boolean>(betaAccepted);
  // Epoch ms when the hourly allowance refreshes; 0 when not blocked. Ticked once a
  // second so the wait visibly counts down and clears itself.
  const [blockedUntil, setBlockedUntil] = useState<number>(readBlockedUntil);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!blockedUntil) return;
    const t = window.setInterval(() => {
      if (Date.now() >= blockedUntil) { setBlockedUntil(0); writeBlockedUntil(0); }
      else tick((n) => n + 1);
    }, 1000);
    return () => window.clearInterval(t);
  }, [blockedUntil]);
  const waitSec = blockedUntil ? Math.ceil((blockedUntil - Date.now()) / 1000) : 0;
  const blocked = waitSec > 0;

  // Tell the server how the answer landed. Best-effort by design: a rating that fails to
  // save is not worth interrupting anyone for, so the thank-you shows either way.
  function rate(rating: 'unhelpful' | 'confusing' | 'helpful') {
    setRated(rating);
    if (!r?.ref || !props.authToken) return;
    void fetch(ASK_BRAIN_URL + '/ask/rating', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${props.authToken}` },
      body: JSON.stringify({ ref: r.ref, rating }),
    }).catch(() => {});
  }

  async function ask(question: string) {
    const text = question.trim();
    if (!text) return;
    setNotice(null); // a stale membership notice must never sit above crisis helplines
    // CRISIS FLOOR, ON DEVICE, BEFORE ANYTHING ELSE. The server runs the same check, but
    // a disclosure of self-harm or danger must never depend on the network: a cold start
    // or a dropped connection would otherwise answer it with "Could not reach the guide
    // right now". Answering here is also the more private path — the words never leave
    // the device. Runs ahead of the sign-in check too, because support does not require
    // a membership.
    if (isCrisis(text)) {
      setQ(text); setErr(''); setLoading(false); setR({ crisis: true });
      // Consented users only, and strictly AFTER the helplines are on screen: send the
      // disclosure up for the research so we can see how often, and in what words, people
      // reach the guide in crisis. Fire-and-forget and never awaited — nothing about the
      // support the person sees depends on it. For everyone else these words never leave
      // the device.
      if (props.researchConsented && props.authToken) {
        void fetch(ASK_BRAIN_URL + '/ask/crisis-log', {
          method: 'POST',
          headers: { 'content-type': 'application/json', authorization: `Bearer ${props.authToken}` },
          body: JSON.stringify({ question: text, research: true }),
        }).catch(() => {});
      }
      return;
    }
    // The /ask endpoint requires a signed-in member (Supabase token). Without one we
    // don't call the server (it would 401) — ask the user to sign in first.
    if (!props.authToken) {
      setQ(text);
      setErr('Please sign in to use the guide.');
      return;
    }
    // Out of questions for this hour: don't spend a request to be told so again. The
    // countdown below is already showing how long is left. (This is AFTER the crisis
    // floor on purpose — being out of questions must never block a risk disclosure.)
    if (blocked) { setQ(text); return; }
    setQ(text); setLoading(true); setErr(''); setNotice(null); setR(null); setRated(null);
    try {
      // POST (not GET) so the typed question stays out of URLs and server logs.
      const res = await fetch(ASK_BRAIN_URL + '/ask', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${props.authToken}` },
        body: JSON.stringify({
          question: text,
          unlocked: props.unlocked,
          hasChildren: props.hasChildren,
          research: props.researchConsented === true,
        }),
      });
      if (res.status === 401) { setErr('Please sign in to use the guide.'); return; }
      // Hourly allowance spent. The server tells us exactly how long is left, so the
      // countdown is honest rather than a guess at the window length.
      if (res.status === 429) {
        const j = await res.json().catch(() => ({} as { retryAfterSec?: number }));
        const until = Date.now() + Math.max(1, Number(j.retryAfterSec) || 3600) * 1000;
        setBlockedUntil(until); writeBlockedUntil(until);
        return;
      }
      if (res.status === 403) {
        const j = await res.json().catch(() => ({} as { error?: string; freeRemaining?: number }));
        setNotice(j && j.error === 'account_suspended' ? 'paused' : 'membership');
        if (j && j.error === 'membership_required' && (j as { freeRemaining?: unknown }).freeRemaining === 0) writeFreeRemaining(props.userId, 0);
        return;
      }
      if (!res.ok) throw new Error('bad status ' + res.status);
      const body = await res.json();
      if (body && typeof body.freeRemaining === 'number') writeFreeRemaining(props.userId, body.freeRemaining);
      setR(body);
    } catch {
      setErr('Could not reach the guide right now. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  }

  // Open real content via the app's own handlers, then close. Result stays live in
  // App state, so re-opening Ask re-shows this answer to pick a different option.
  function open(c: Card) {
    if (c.kind === 'lesson') props.onOpenLesson(c.id);
    else if (c.kind === 'read') props.onOpenArticle(c.id);
    else if (c.kind === 'tool') props.onOpenTool(c.id);
    else if (c.kind === 'breathe') props.onOpenBreath(c.id);
    else if (c.kind === 'meditation') props.onOpenMeditation(c.id);
    props.onClose();
  }

  // Clear the box and start again: drops the typed question, the answer and any error
  // so the example chips come back. Query/result live in App state (so Ask can be
  // reopened on the same answer), which is why this clears there rather than locally.
  function reset() {
    setQ('');
    setR(null);
    setErr('');
    setRated(null);
  }

  const titles = (o: any): string[] => o?.titles || (o?.title ? [o.title] : []);

  return (
    <div style={S.overlay}>
      <div style={TRIAGE_V2 ? { ...S.head, paddingTop: 'calc(var(--safe-top) + 60px)' } : S.head}>
        <img src="/newbuttonimages/askrenurture.png" alt="" aria-hidden decoding="async" style={S.headImg} />
        {TRIAGE_V2
          ? <BackButton floating onClick={props.onClose} />
          : <button style={S.close} onClick={props.onClose} aria-label="Close">✕</button>}
        <div style={S.headContent}>
          <div style={S.brandRow}>
            <span style={S.brand}>Ask ReNurture</span>
          </div>
          <div style={S.sub}>{TRIAGE_V2 ? 'Tell me what you need and I’ll point you to the lessons, tools and meditations that fit.' : 'ReNurture holds a lot — lessons, tools, meditations and reads. Tell me what you need and this guide will help you find what fits.'}</div>
          <div style={S.subTrust}>Everything suggested was created by a Clinical Psychologist, based on what works in practice.</div>
        </div>
      </div>

      {/* FIRST USE. Before anyone types anything, say plainly what this is and what it is
          not. It is new, it points at existing content rather than advising, it is not a
          crisis service, and the words are sent to an external AI provider. Shown once
          per device; nothing else in the panel is reachable until it is acknowledged. */}
      {!beta ? (
        <div style={S.body}>
          <div style={S.betaBox}>
            <div style={S.betaEyebrow}>Before you start</div>
            <h2 style={S.betaTitle}>This is in beta, and still being built</h2>
            <p style={S.betaP}>
              It points you to lessons, tools and readings already written for the programme. It does not
              give advice, it is not therapy, and it is not a crisis service. In an emergency call 999.
            </p>
            <p style={S.betaP}>
              It can be wrong, so nothing it suggests is an instruction. Your words go to an external AI
              service to find the right content, so please do not use real names.
            </p>
            <button style={S.betaBtn} onClick={() => { acceptBeta(); setBeta(true); }}>
              I understand, continue
            </button>
            <button style={S.betaBack} onClick={props.onClose}>Not now</button>
          </div>
        </div>
      ) : (

      <div style={S.body}>
        <div style={S.inputRow}>
          <input
            style={S.input}
            value={q}
            placeholder="What do you need?"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') ask(q); }}
          />
          {(q || r || err) && (
            <button style={S.reset} onClick={reset} aria-label="Clear and start again" title="Clear and start again">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 1 2.64 6.36" />
                <path d="M3 20v-5h5" />
              </svg>
            </button>
          )}
          <button
            style={blocked ? { ...S.go, ...S.goOff } : S.go}
            onClick={() => ask(q)}
            disabled={blocked}
            aria-disabled={blocked}
          >
            Ask
          </button>
        </div>

        {/* The instruction carries the warning colour; the disclosure that follows it is
            muted, so the user still learns where their words go without the whole line
            reading as an alarm. */}
        <div style={S.aiNotice}>
          Do not enter any real names.{' '}
          <span style={S.aiNoticeSoft}>Questions are answered with help from an external AI service.</span>
        </div>

        {/* Out of questions for this hour. Says plainly what happened and when it comes
            back, counting down as it goes, rather than failing with a vague error. The
            example chips are hidden while blocked so nothing invites a tap that cannot
            work. Note this never affects the crisis floor: that answers on device. */}
        {blocked && (
          <div style={S.waitBox}>
            <div style={S.waitTitle}>That’s your questions for this hour</div>
            <div style={S.waitBody}>
              The guide refreshes in <b>{describeWait(waitSec)}</b>. Everything else in the app is open as usual, and the Calm tools are always there.
            </div>
          </div>
        )}

        {!r && !loading && !blocked && (
          <div style={S.chips}>
            {EXAMPLES.map((ex) => (
              <button key={ex} style={S.chip} onClick={() => ask(ex)}>{ex}</button>
            ))}
          </div>
        )}

        {loading && <div style={S.dim}>Thinking…</div>}
        {err && <div style={S.err}>{err}</div>}
        {notice && (
          <div style={S.notice}>
            <div>{notice === 'paused' ? 'Your account is paused. Please contact support.' : 'Ask ReNurture is part of membership.'}</div>
            {notice === 'membership' && props.onUnlock && (
              <button style={{ ...S.betaBtn, marginTop: 12 }} onClick={props.onUnlock}>See membership</button>
            )}
          </div>
        )}

        {r && r.crisis && (
          <div style={S.crisis}>
            <div style={S.crisisEyebrow}>You deserve real support</div>
            <h3 style={S.crisisH}>Let's get you proper support first</h3>
            <p style={S.crisisP}>Some things deserve a real person, straight away.</p>
            <ul style={S.crisisUl}>
              <li><b>Emergency:</b> call <b>999</b> if you are in danger now</li>
              <li><b>Samaritans:</b> <b>116 123</b></li>
              <li><b>Shout:</b> text <b>SHOUT to 85258</b></li>
              <li><b>National Domestic Abuse Helpline:</b> <b>0808 2000 247</b></li>
            </ul>
          </div>
        )}

        {r && !r.crisis && (r.unsure || !r.lead) && (
          <div style={S.dim}>I'm not certain what fits those words. Try an example, or browse the Library and settle tools — always here.</div>
        )}

        {r && !r.crisis && !r.unsure && r.lead && (
          <div>
            {r.needs?.length > 0 && (
              <div style={S.needsRow}>
                <span style={S.needsLabel}>Sounds like this is about</span>
                {r.needs.map((n: any) => (<span key={n.need} style={S.need}>{n.need}</span>))}
              </div>
            )}

            {r.intro && (
              <p style={S.intro}>{r.intro.replace(/[.\s]+$/, '')}. Here's where I'd start you —</p>
            )}

            {(() => {
              const t = kindOf(r.lead.kind);
              return (
                <button style={S.leadCard} onClick={() => open(r.lead)}>
                  <span style={{ ...S.eyebrow, color: t.accent }}>{t.label} · start here</span>
                  <div style={S.leadTitle}>{cleanTitle(r.lead.title)}</div>
                  {r.lead.why && <div style={S.why}>{r.lead.why}</div>}
                  <span style={{ ...S.openBtn, background: t.accent }}>Open</span>
                </button>
              );
            })()}

            {r.alternates?.length > 0 && (
              <div style={S.altWrap}>
                <div style={S.altLabel}>or, if you'd rather</div>
                {r.alternates.map((a: Card) => {
                  const t = kindOf(a.kind);
                  return (
                    <button key={a.id} style={{ ...S.alt, borderLeft: `4px solid ${t.accent}` }} onClick={() => open(a)}>
                      <span style={{ ...S.altKind, color: t.accent, background: t.soft }}>{t.label}</span>
                      <span style={S.altTitle}>{cleanTitle(a.title)}</span>
                      <span style={S.altArrow} aria-hidden>›</span>
                    </button>
                  );
                })}
              </div>
            )}

            {r.programmeLocked && (
              <div style={S.deeper}>
                <div style={S.deeperEyebrow}>Coming up in your programme</div>
                <p style={S.deeperBody}>
                  {r.programmeLocked.note ? r.programmeLocked.note.replace(/[.\s]+$/, '') + '. ' : ''}
                  You'll come to this further on, in <b style={S.deeperTitles}>{joinAnd(titles(r.programmeLocked))}</b>.
                </p>
              </div>
            )}
            {/* Closure work (a goodbye letter, forgiveness, letting go). Named explicitly
                as PRIVATE and for LATER, because "Writing a Goodbye Letter to Your Ex"
                reads at a glance like something you do WITH the ex. It is the opposite:
                written for yourself, never sent, and the ex is not involved. */}
            {r.comingUp && (
              <div style={S.deeper}>
                <div style={S.deeperEyebrow}>Later, when you're ready</div>
                <p style={S.deeperBody}>
                  {r.comingUp.note ? r.comingUp.note.replace(/[.\s]+$/, '') + '. ' : ''}
                  <b style={S.deeperTitles}>{joinAnd(titles(r.comingUp))}</b> {titles(r.comingUp).length > 1 ? 'are' : 'is'} closure work that comes further on.
                  {' '}You do {titles(r.comingUp).length > 1 ? 'them' : 'it'} privately, for yourself. Nothing is sent, and your ex is not involved.
                </p>
              </div>
            )}

            {r.support && (
              <p style={S.support}>If the heaviness feels like too much to carry alone, you don't have to. Talking it through with someone you trust, or Samaritans on <b>116 123</b>, can help.</p>
            )}

            {r.settle && (
              <button style={S.calm} onClick={props.onOpenCalm}>
                <span style={S.calmChip} aria-hidden><span style={S.calmDot} /></span>
                <span style={S.calmText}>
                  <span style={S.calmTitle}>Calm</span>
                  <span style={S.calmSub}>Choose a feeling and settle your body — a real part of early recovery</span>
                </span>
                <span style={S.calmArrow} aria-hidden>→</span>
              </button>
            )}

            {/* How did that land? Shown ONLY when the server captured the question
                (r.ref), which means the user has consented to research — so nobody is
                asked to rate an answer that was never stored. Never shown on the crisis
                screen: someone in that state is not here to give us feedback. */}
            {r.ref && (
              <div style={S.rate}>
                {rated ? (
                  <div style={S.rateThanks}>Thank you, that helps us make the guide better.</div>
                ) : (
                  <>
                    <div style={S.rateLabel}>Was this helpful?</div>
                    <div style={S.rateRow}>
                      <button style={S.rateBtn} onClick={() => rate('unhelpful')}>Unhelpful</button>
                      <button style={S.rateBtn} onClick={() => rate('confusing')}>Confusing</button>
                      <button style={S.rateBtn} onClick={() => rate('helpful')}>Helpful</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {r && !r.crisis && typeof r.freeRemaining === 'number' && (
          <div style={S.freeLeft}>
            {r.freeRemaining > 0
              ? `${r.freeRemaining} free question${r.freeRemaining === 1 ? '' : 's'} left`
              : 'That was your last free question.'}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  overlay: { position: 'absolute', inset: 0, background: 'var(--cream, #f7f1e6)', zIndex: 60, display: 'flex', flexDirection: 'column', animation: 'rise .28s ease' },
  head: { padding: 'calc(var(--safe-top) + 22px) 21px 15px', borderBottom: '1px solid var(--hair, rgba(44,42,37,0.1))', position: 'relative', background: 'linear-gradient(160deg, #e7eee1, #dde4d6)', overflow: 'hidden' },
  // The Ask ReNurture motif, faded across the bar (left-transparent so the text stays
  // readable), matching the arrival-tile overlay treatment. 40% opacity per spec.
  headImg: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4, pointerEvents: 'none', zIndex: 0, WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 45%, #000 82%)', maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 45%, #000 82%)' },
  headContent: { position: 'relative', zIndex: 1 },
  close: { position: 'absolute', top: 'calc(var(--safe-top) + 8px)', right: 8, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', fontSize: 25, fontWeight: 700, color: 'var(--ink, #2c2a25)', cursor: 'pointer', lineHeight: 1, zIndex: 2, WebkitTapHighlightColor: 'transparent' },
  brandRow: { display: 'flex', alignItems: 'center', gap: 9 },
  brand: { fontFamily: SERIF, fontSize: 25, letterSpacing: '-0.01em', color: 'var(--green-deep, #167a5a)', fontWeight: 500 },
  // Says "still being built" wherever the feature appears, without shouting it.

  betaBox: { background: 'linear-gradient(180deg,#fffdf6,#fff4e2)', border: '1px solid var(--hair, rgba(44,42,37,0.1))', borderRadius: 20, padding: '20px 20px 18px', boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92))' },
  betaEyebrow: { fontFamily: UI, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--amber-deep, #9b6712)', marginBottom: 8 },
  betaTitle: { fontFamily: SERIF, fontSize: 21, fontWeight: 400, lineHeight: 1.22, color: 'var(--ink, #2c2a25)', margin: '0 0 14px' },
  betaP: { fontFamily: UI, fontSize: 13.5, lineHeight: 1.6, color: 'var(--ink-soft, #5a554c)', margin: '0 0 12px' },
  betaBtn: { width: '100%', border: 'none', borderRadius: 999, cursor: 'pointer', background: 'var(--green-deep, #167a5a)', color: '#fff', fontFamily: UI, fontSize: 15, fontWeight: 600, padding: '14px 20px', boxShadow: 'var(--sh1, 0 8px 18px -12px rgba(64,46,22,.2))' },
  betaBack: { width: '100%', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink-soft, #5a554c)', fontFamily: UI, fontSize: 13.5, padding: '12px 0 0' },
  sub: { marginTop: 6, fontSize: 13, color: 'var(--ink-soft, #5a554c)', fontFamily: UI, lineHeight: 1.45, maxWidth: 440 },
  subTrust: { marginTop: 5, fontSize: 12, color: 'var(--green-deep, #167a5a)', fontFamily: UI, fontWeight: 600, lineHeight: 1.4, maxWidth: 440 },
  body: { flex: 1, overflowY: 'auto', padding: '18px 21px 40px' },

  inputRow: { display: 'flex', gap: 9 },
  aiNotice: { fontFamily: UI, fontSize: 12, lineHeight: 1.35, color: '#b3261e', marginTop: 8 },
  aiNoticeSoft: { color: 'var(--ink-soft, #5a554c)' },
  input: { flex: 1, fontFamily: UI, padding: '13px 15px', borderRadius: 14, border: '1px solid var(--hair, rgba(44,42,37,0.1))', fontSize: 15, background: 'linear-gradient(180deg,#ffffff,#fffcf6)', color: 'var(--ink, #2c2a25)', boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92))' },
  go: { fontFamily: UI, padding: '0 20px', borderRadius: 14, border: 'none', background: 'var(--green-deep, #167a5a)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: 'var(--sh1, 0 8px 18px -12px rgba(64,46,22,.2))' },
  reset: { display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: '0 14px', borderRadius: 14, border: '1px solid var(--hair, rgba(44,42,37,0.1))', background: 'linear-gradient(180deg,#fffdf6,#fff4e2)', color: 'var(--ink-soft, #5a554c)', cursor: 'pointer', boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92))' },

  chips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 15 },
  chip: { fontFamily: UI, fontSize: 12.5, padding: '8px 13px', borderRadius: 999, border: '1px solid var(--hair, rgba(44,42,37,0.1))', background: 'linear-gradient(180deg,#fffdf6,#fff4e2)', color: 'var(--ink-soft, #5a554c)', cursor: 'pointer', boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92))' },

  dim: { marginTop: 20, color: 'var(--ink-soft, #5a554c)', fontFamily: UI, fontSize: 14, lineHeight: 1.5 },
  notice: { marginTop: 20, color: 'var(--ink-soft, #5a554c)', fontFamily: UI, fontSize: 14.5, background: 'linear-gradient(180deg,#fffdf6,#fff4e2)', padding: 15, borderRadius: 13, border: '1px solid var(--hair, rgba(44,42,37,0.1))' },
  freeLeft: { marginTop: 14, fontFamily: UI, fontSize: 12.5, color: 'var(--ink-soft, #5a554c)' },
  err: { marginTop: 20, color: '#8f3a28', fontFamily: UI, fontSize: 13.5, background: '#fdf0ee', padding: 13, borderRadius: 13 },

  intro: { margin: '13px 0 2px', fontFamily: SERIF, fontSize: 16.5, lineHeight: 1.4, fontWeight: 400, color: 'var(--ink, #2c2a25)' },
  needsRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7, marginTop: 18 },
  needsLabel: { fontFamily: UI, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-soft, #5a554c)', opacity: 0.75 },
  need: { fontFamily: UI, fontSize: 12.5, background: 'rgba(22,122,90,.1)', color: 'var(--green-deep, #167a5a)', fontWeight: 600, borderRadius: 999, padding: '4px 11px' },

  leadCard: { display: 'block', width: '100%', textAlign: 'left', marginTop: 15, padding: '20px 20px 22px', borderRadius: 22, border: '1px solid var(--hair, rgba(44,42,37,0.1))', background: 'linear-gradient(180deg,var(--card2-top,#fffdf6),var(--card2-bot,#fff4e2))', boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92)), var(--sh1, 0 1px 1px rgba(64,46,22,.045),0 8px 18px -12px rgba(64,46,22,.2))', cursor: 'pointer' },
  eyebrow: { fontFamily: UI, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' },
  leadTitle: { fontFamily: SERIF, fontSize: 26, lineHeight: 1.12, fontWeight: 400, color: 'var(--ink, #2c2a25)', margin: '9px 0 5px' },
  why: { fontFamily: UI, fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-soft, #5a554c)' },
  openBtn: { display: 'inline-block', marginTop: 15, padding: '9px 22px', borderRadius: 999, color: '#fff', fontFamily: UI, fontSize: 14, fontWeight: 600, boxShadow: 'var(--sh1, 0 8px 18px -12px rgba(64,46,22,.2))' },

  altWrap: { marginTop: 20 },
  altLabel: { fontFamily: UI, fontSize: 12, color: 'var(--ink-soft, #5a554c)', marginBottom: 9, opacity: 0.85 },
  alt: { display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 12, padding: '13px 14px', borderRadius: 15, border: '1px solid var(--hair, rgba(44,42,37,0.1))', background: 'linear-gradient(180deg,var(--card-top,#ffffff),var(--card-bot,#fffcf6))', boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92)), var(--sh1, 0 8px 18px -12px rgba(64,46,22,.2))', cursor: 'pointer', marginBottom: 10 },
  altKind: { fontFamily: UI, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 8px', borderRadius: 7, minWidth: 52, textAlign: 'center' },
  altTitle: { flex: 1, fontFamily: SERIF, fontSize: 15.5, fontWeight: 500, color: 'var(--ink, #2c2a25)', lineHeight: 1.25 },
  altArrow: { fontFamily: UI, fontSize: 20, color: 'var(--ink-soft, #5a554c)', opacity: 0.45 },

  deeper: { marginTop: 18, background: 'var(--surface1, #f2ecdd)', border: '1px solid var(--hair, rgba(44,42,37,0.1))', borderRadius: 15, padding: '14px 16px' },
  deeperEyebrow: { fontFamily: UI, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--amber-deep, #9b6712)', marginBottom: 6 },
  deeperBody: { margin: 0, fontFamily: UI, fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-soft, #5a554c)' },
  deeperTitles: { color: 'var(--ink, #2c2a25)', fontWeight: 600 },

  support: { marginTop: 16, fontFamily: UI, fontSize: 13, lineHeight: 1.55, color: 'var(--ink-soft, #5a554c)' },

  calm: { display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 13, marginTop: 18, padding: '13px 15px', borderRadius: 16, border: '1px solid rgba(53,98,117,.22)', background: 'linear-gradient(180deg,#f4f8f9,#e9f0f2)', cursor: 'pointer', boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92))' },
  calmChip: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 13, background: 'rgba(53,98,117,.14)', flexShrink: 0 },
  calmDot: { width: 15, height: 15, borderRadius: 999, background: '#356275', boxShadow: '0 0 0 4px rgba(53,98,117,.22)' },
  calmText: { display: 'flex', flexDirection: 'column', flex: 1, gap: 2 },
  calmTitle: { fontFamily: SERIF, fontSize: 17, fontWeight: 500, color: '#2b4a57' },
  calmSub: { fontFamily: UI, fontSize: 12, lineHeight: 1.4, color: '#4d6570' },
  calmArrow: { fontFamily: UI, fontSize: 18, color: '#356275' },

  goOff: { opacity: 0.45, cursor: 'default', boxShadow: 'none' },
  // The hourly-allowance notice. Warm, not an error: nothing has gone wrong.
  waitBox: { marginTop: 18, background: 'var(--surface1, #f2ecdd)', border: '1px solid var(--hair, rgba(44,42,37,0.1))', borderRadius: 16, padding: '15px 16px' },
  waitTitle: { fontFamily: SERIF, fontSize: 17, fontWeight: 500, color: 'var(--ink, #2c2a25)', marginBottom: 5 },
  waitBody: { fontFamily: UI, fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-soft, #5a554c)' },

  // Quiet by design: a research prompt must not compete with the content it is asking
  // about, so no card, no colour, just a hairline rule and three plain chips.
  rate: { marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--hair, rgba(44,42,37,0.1))' },
  rateLabel: { fontFamily: UI, fontSize: 12.5, color: 'var(--ink-soft, #5a554c)', marginBottom: 9 },
  rateRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  rateBtn: { fontFamily: UI, fontSize: 12.5, padding: '8px 14px', borderRadius: 999, border: '1px solid var(--hair, rgba(44,42,37,0.1))', background: 'linear-gradient(180deg,#fffdf6,#fff4e2)', color: 'var(--ink-soft, #5a554c)', cursor: 'pointer', boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92))' },
  rateThanks: { fontFamily: UI, fontSize: 12.5, lineHeight: 1.5, color: 'var(--green-deep, #167a5a)' },

  crisis: { marginTop: 20, background: '#fdf0ee', border: '1px solid rgba(180,69,47,.3)', borderRadius: 18, padding: 20, boxShadow: 'var(--edge, inset 0 1px 0 rgba(255,255,255,.92))' },
  crisisEyebrow: { fontFamily: UI, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#b4452f', marginBottom: 8 },
  crisisH: { fontFamily: SERIF, margin: '0 0 8px', fontSize: 21, fontWeight: 400, color: '#8f3a28' },
  crisisP: { fontFamily: UI, fontSize: 13.5, color: '#5c4a45', margin: 0 },
  crisisUl: { fontFamily: UI, fontSize: 13.5, color: '#5c4a45', lineHeight: 1.8, margin: '10px 0 0', paddingLeft: 18 },
};
